import {transform} from 'lodash';

import {PaymentMethodTypes, PaymentMethod} from '~/shared/consts/paymentConsts';

import {Payment} from '../../store/models';

import GooglePayCard from './cardsPie/gpay.svg?react';
import ApplePayCard from './cardsPie/applePayCard.svg?react';
import ApplePayCardSmall from './cardsPie/applePayCardSmall.svg?react';
import IsracardIcon from './cardsPie/isracard.svg?react';
import IsracardIconSmall from './cardsPie/isracardSmall.svg?react';
import AmexSmallIcon from './cardsPie/amexSmall.svg?react';
import MastercardIcon from './cardsPie/mastercard.svg?react';
import MastercardSmallIcon from './cardsPie/mastercardSmall.svg?react';
import VisaIcon from './cardsPie/visa.svg?react';
import VisaSmallIcon from './cardsPie/visaSmall.svg?react';
import PayPalIcon from './cardsPie/paypal.svg?react';
import PayPalSmallIcon from './cardsPie/paypalSmall.svg?react';
import MoneyCardIcon from './cardsPie/moneycard.svg?react';
import MoneyCardSmallIcon from './cardsPie/moneycardSmall.svg?react';
import CashIcon from './cardsPie/cash.svg?react';
import CashSmallIcon from './cardsPie/cashSmall.svg?react';
import DinersIcon from './cardsPie/diners.svg?react';
import DinersSmallIcon from './cardsPie/dinersSmall.svg?react';
import TenbisCredit from './cardsPie/tenbisCredit.svg?react';
import TenbisCreditSmall from './cardsPie/tenbisCreditSmall.svg?react';
import AmexIcon from './cardsPie/amex.svg?react';
import CreditCardSmallIcon from './cardsPie/creditCardIconSmall.svg?react';
import CreditCardIcon from './cardsPie/creditCardIcon.svg?react';
import MaestroCardIcon from './cardsPie/maestro.svg?react';
import MaestroCardIconSmall from './cardsPie/maestroSmall.svg?react';

export const paymentMethodIsCreditCard = (paymentMethod: PaymentMethod, creditCardType?: string) => {
  return creditCardType !== 'none' && paymentMethod.toLowerCase() === PaymentMethodTypes.CREDIT_CARD.toLowerCase();
};

export const paymentMethodIsMoneyCard = (paymentMethod: PaymentMethod) => {
  return paymentMethod.toLowerCase() === PaymentMethodTypes.MONEY_CARD.toLowerCase();
};

export const paymentMethodIsPayPal = (paymentMethod: PaymentMethod) => {
  return paymentMethod.toLowerCase() === PaymentMethodTypes.PAYPAL.toLowerCase();
};

export const paymentMethodIsCash = (paymentMethod: PaymentMethod) => {
  return paymentMethod.toLowerCase() === PaymentMethodTypes.CASH.toLowerCase();
};
export const paymentMethodIsApplePay = (paymentMethod: PaymentMethod) => {
  return paymentMethod.toLowerCase() === PaymentMethodTypes.APPLE_PAY.toLowerCase();
};
export const paymentMethodIsGooglePay = (paymentMethod: PaymentMethod) => {
  return paymentMethod.toLowerCase() === PaymentMethodTypes.GOOGLE_PAY.toLowerCase();
};

export const creditCardTypesEnum: Record<string, string> = {
  amex: 'Amex',
  diners: 'Diners',
  creditcardicon: 'CreditCardIcon',
  mastercard: 'Mastercard',
  visa: 'Visa',
  newcreditcard: 'NewCreditCard',
  paypal: 'PayPal',
  moneycard: 'MoneyCard',
  cash: 'Cash',
  credit: 'Credit',
  applepay: 'ApplePay',
  none: 'NewCreditCard',
  unknown: 'NewCreditCard',
  isracard: 'Isracard',
  maestro: 'Maestro',
  googlepay: 'GooglePay',
};

const creditCardNameToIconNameMapping: Record<string, string> = transform(
  {
    amex: ['amex'],
    diners: ['diners'],
    creditCardIcon: ['discover', 'maestro', 'switch', 'solo', 'jcb', 'carteblanche', 'chinaunionpay', 'laser'],
    mastercard: ['isracard', 'mastercard'],
    visa: ['visa', 'leumicard', 'cal', 'creditcard'],
    newCreditCard: ['default'],
  },
  (result: typeof creditCardNameToIconNameMapping, cardNames, cardIconName) => {
    cardNames.forEach(cardName => {
      result[cardName] = cardIconName;
    });
  },
  {},
);

export const paymentCardsIconComponents = {
  [creditCardTypesEnum.amex]: AmexIcon,
  [creditCardTypesEnum.diners]: DinersIcon,
  [creditCardTypesEnum.creditcardicon]: CreditCardIcon,
  [creditCardTypesEnum.mastercard]: MastercardIcon,
  [creditCardTypesEnum.visa]: VisaIcon,
  [creditCardTypesEnum.newcreditcard]: CreditCardIcon,
  [creditCardTypesEnum.paypal]: PayPalIcon,
  [creditCardTypesEnum.moneycard]: MoneyCardIcon,
  [creditCardTypesEnum.cash]: CashIcon,
  [creditCardTypesEnum.credit]: TenbisCredit,
  [creditCardTypesEnum.applepay]: ApplePayCard,
  [creditCardTypesEnum.isracard]: IsracardIcon,
  [creditCardTypesEnum.maestro]: MaestroCardIcon,
  [creditCardTypesEnum.googlepay]: GooglePayCard,
};

export const paymentCardsSmallIconComponents = {
  [creditCardTypesEnum.amex]: AmexSmallIcon,
  [creditCardTypesEnum.diners]: DinersSmallIcon,
  [creditCardTypesEnum.creditcardicon]: CreditCardSmallIcon,
  [creditCardTypesEnum.mastercard]: MastercardSmallIcon,
  [creditCardTypesEnum.visa]: VisaSmallIcon,
  [creditCardTypesEnum.newcreditcard]: CreditCardSmallIcon,
  [creditCardTypesEnum.paypal]: PayPalSmallIcon,
  [creditCardTypesEnum.moneycard]: MoneyCardSmallIcon,
  [creditCardTypesEnum.cash]: CashSmallIcon,
  [creditCardTypesEnum.credit]: TenbisCreditSmall,
  [creditCardTypesEnum.applepay]: ApplePayCardSmall,
  [creditCardTypesEnum.isracard]: IsracardIconSmall,
  [creditCardTypesEnum.maestro]: MaestroCardIconSmall,
  [creditCardTypesEnum.googlepay]: GooglePayCard,
};

export const getPaymentType = (paymentMethod: PaymentMethod, creditCardType?: string) => {
  const isCreditCard = paymentMethodIsCreditCard(paymentMethod, creditCardType);
  const isMoneyCard = paymentMethodIsMoneyCard(paymentMethod);
  const isCash = paymentMethodIsCash(paymentMethod);
  const isPaypal = paymentMethodIsPayPal(paymentMethod);
  const isApplePay = paymentMethodIsApplePay(paymentMethod);
  const isGooglePay = paymentMethodIsGooglePay(paymentMethod);
  return {isCreditCard, isMoneyCard, isCash, isPaypal, isApplePay, isGooglePay};
};

export const getCreditCardName = ({paymentMethod, isTenbisCredit, creditCardType}: {paymentMethod: PaymentMethod; isTenbisCredit: boolean; creditCardType?: string}) => {
  const {isCreditCard, isMoneyCard, isCash, isPaypal, isApplePay, isGooglePay} = getPaymentType(paymentMethod, creditCardType);

  if (isTenbisCredit) {
    return PaymentMethodTypes.CREDIT;
  }
  if (isCreditCard) {
    if (creditCardType) {
      return creditCardNameToIconNameMapping[
        creditCardType.toLowerCase() as keyof typeof creditCardNameToIconNameMapping
      ];
    }
    return creditCardTypesEnum.creditcardicon;
  }

  if (isMoneyCard) {
    return PaymentMethodTypes.MONEY_CARD;
  }
  if (isCash) {
    return PaymentMethodTypes.CASH;
  }
  if (isPaypal) {
    return PaymentMethodTypes.PAYPAL;
  }
  if (isApplePay) {
    return PaymentMethodTypes.APPLE_PAY;
  }
  if (isGooglePay) {
    return PaymentMethodTypes.GOOGLE_PAY;
  }
  return PaymentMethodTypes.UNKNOWN;
};
  
export const getPaymentCardIconComponent = (
  paymentMethod: PaymentMethod,
  creditCardType: keyof typeof creditCardNameToIconNameMapping,
  isTenbisCredit: boolean,
  small = false,
) => {
  if (paymentMethod === PaymentMethodTypes.APPLE_PAY.toLowerCase()) {
    return ApplePayCard;
  }

  if (paymentMethod === PaymentMethodTypes.GOOGLE_PAY.toLowerCase()) {
    return GooglePayCard;
  }

  const cardName = getCreditCardName({paymentMethod, isTenbisCredit, creditCardType});
  const creditCardName = creditCardTypesEnum[cardName.toLowerCase() as keyof typeof creditCardTypesEnum];
  return small ? paymentCardsSmallIconComponents[creditCardName] : paymentCardsIconComponents[creditCardName];
};

export const filterPayments = (paymentsList: Payment[], searchValue: string) => {
  let filteredPayments = paymentsList;
  if (searchValue) {
    const lowerCaseSearchValue = searchValue.toLowerCase();
    filteredPayments = filteredPayments.filter(
      payment =>
        payment.userName?.toLowerCase().includes(lowerCaseSearchValue) ||
        payment.cardLastDigits?.includes(lowerCaseSearchValue),
    );
  }
  return filteredPayments;
};

const sortingAvailablePaymentsOrder = {
  [PaymentMethodTypes.CREDIT]: 1,
  [PaymentMethodTypes.CREDIT_CARD]: 2,
  [PaymentMethodTypes.PAYPAL]: 3,
  [PaymentMethodTypes.MONEY_CARD]: 4,
  [PaymentMethodTypes.CASH]: 5,
};

const checkIfCredit = (payment: Payment) => {
  if (payment.isTenbisCredit) {
    return PaymentMethodTypes.CREDIT;
  }
  return payment.paymentMethod;
};

export const sortPayments = (paymentsList: Payment[]) =>
  paymentsList.sort(
    (a: Payment, b: Payment) => {
      const APaymentMethod = checkIfCredit(a);
      const BPaymentMethod = checkIfCredit(b);

      return (
        sortingAvailablePaymentsOrder[APaymentMethod as keyof typeof sortingAvailablePaymentsOrder] -
        sortingAvailablePaymentsOrder[BPaymentMethod as keyof typeof sortingAvailablePaymentsOrder]
      );
    },
  );
